/* eslint-disable @typescript-eslint/no-explicit-any */
// This file handles complex dynamic filter data structures from various sources
// Using 'any' is appropriate here for maximum flexibility with filter configurations

export function useCatalogFilters(meta: any) {
  const { public: config } = useRuntimeConfig()
  const ignoreParamsPattern = new RegExp(config.ignoreParamsPattern)

  const applyOptionsDirectly = useState('applyOptionsDirectly', () => false)
  const rawFilters = useState<any[]>('rawFilters', () => [])
  const filters = useState<any[]>('filters', () => shallowRef([]))
  const activeFilters = useState<any[]>('activeFilters', () => [])
  const activeFiltersCount = useState<number>('activeFiltersCount', () => 0)

  watch(rawFilters, () => {
    filters.value = [...rawFilters.value]?.filter(filter => !!filter && !filter.isHidden)?.filter(filter => !(filter.options && !filter.options.length))?.map((filter) => {
      const _filter = {
        ...filter,
      }

      _filter.activeOptionsCount = 0
      _filter.label = _filter.label.charAt(0).toUpperCase() + _filter.label.slice(1)

      if (_filter.options?.length) {
        _filter.options = _filter.options.map((option: any) => {
          return {
            ...option,
            selected: option.active,
          }
        })?.sort((a: any, b: any) => {
          return a.position - b.position
        })

        _filter.activeOptionsCount = _filter.options.filter((option: any) => option.active)?.length || 0
      }

      if (_filter.type === 'range') {
        _filter.minSelected = _filter.minSelected ?? _filter.min
        _filter.maxSelected = _filter.maxSelected ?? _filter.max
        _filter.minToBeSelected = _filter.minToBeSelected ?? _filter.minSelected
        _filter.maxToBeSelected = _filter.maxToBeSelected ?? _filter.maxSelected
        _filter.activeOptionsCount += Math.floor(_filter.min) !== Math.floor(_filter.minSelected) ? 1 : 0
        _filter.activeOptionsCount += Math.ceil(_filter.max) !== Math.ceil(_filter.maxSelected) ? 1 : 0
      }

      _filter.isExpanded = _filter.isExpanded || _filter.activeOptionsCount > 0

      if (_filter.key.includes('brand')) {
        _filter.type = 'default'
      }

      // if (_filter.key.includes('color')) {
      //   _filter.type = 'visual'
      // }

      return _filter
    })?.sort((a: any, b: any) => {
      return a.position - b.position
    }) || []
  }, {
    immediate: true,
  })

  const getDeepestActiveOptions = (options: any[]): any | null => {
    const deepest: any[] = []
    options?.forEach((option: any) => {
      if (option.active && !option.children?.length) {
        deepest.push(option)
      }
      else if (option.active) {
        const childDeepest = getDeepestActiveOptions(option.children)
        if (childDeepest.length) {
          deepest.push(...childDeepest)
        }
        else {
          deepest.push(option)
        }
      }
    })
    return deepest
  }

  watch(filters, () => {
    activeFilters.value = [
      ...filters.value,
    ].filter(filter => filter.activeOptionsCount > 0).map((f) => {
      const filter = { ...f }
      const urlFilter = meta.value?.filters?.find((urlFilter: any) => urlFilter.key === filter.key)

      delete filter.position
      delete filter.hasUrl
      delete filter.isHidden
      delete filter.isExpanded
      delete filter.numberUnfoldedOptions
      delete filter.limitOptionsShowSearchBox
      delete filter.isShowSearchBox

      if (filter.type === 'range') {
        if (filter.min === filter.minSelected) {
          delete filter.minSelected
        }
        delete filter.min
        delete filter.minToBeSelected

        if (filter.max === filter.maxSelected) {
          delete filter.maxSelected
        }
        delete filter.max
        delete filter.maxToBeSelected
      }

      if (filter.options?.length) {
        filter.options = getDeepestActiveOptions(filter.options)
      }

      if (urlFilter && ['category', 'cat'].includes(filter.key)) {
        // Remove categories already in URL path
        if (filter.options.every((option: any) => urlFilter.values.includes(option.key))) {
          return null
        }
      }

      return filter
    }).filter(f => f !== null)
  }, {
    immediate: true,
  })

  watch(activeFilters, () => {
    activeFiltersCount.value = activeFilters.value.length || 0
  }, {
    immediate: true,
  })

  const expectedTotal = useState('expectedTotal', () => 0)

  const route = useRoute()
  const router = useRouter()
  const { search } = useCatalogSearch()
  const { defaultSort, sort, defaultItemsPerPage, itemsPerPage, totalCount } = useCatalogPaginator()

  expectedTotal.value = totalCount.value

  const filtersFromRoute = computed(() => {
    const params: any[] = [
      ...(meta.value?.filters?.map((filter: any) => {
        const { __typename, ...remainingFilterData } = filter
        return remainingFilterData
      }) || []),
    ]

    Object.entries(route.query).forEach(([filterKey, filterValue]) => {
      // console.log('filterKey', filterKey)
      // console.log('filterValue', filterValue)

      if (!filterValue) {
        return
      }

      if (ignoreParamsPattern.test(filterKey)) {
        return
      }

      const filterValues = Array.isArray(filterValue) ? filterValue : String(filterValue).split('~').map((filterValue: string) => decodeURIComponent(filterValue))
      const filter: any = {
        key: filterKey,
        values: filterValues,
      }
      if (filterValues.length === 2 && filterValues.every((val: any) => !isNaN(+val))) {
        filter.min = parseFloat(String(filterValues[0]))
        filter.max = parseFloat(String(filterValues[1]))
      }
      params.push(filter)
    })

    return Object.values(params.reduce((acc: any, item: any) => {
      acc[item.key] = acc[item.key] === undefined
        ? item
        : {
            ...acc[item.key],
            ...item,
            values: [
              ...acc[item.key].values,
              ...item.values,
            ],
          }
      return acc
    }, {}))
  })

  function generateUrlFromSelectedFilters() {
    const basePathPieces = route?.path?.replace(/^\/|\/$/g, '')?.split('/') || []
    const lastBasePathPiece = basePathPieces[basePathPieces.length - 1]
    const params = new URLSearchParams()

    let setFilter = false
    params.set('filter', '1')

    if (search.value) {
      params.set('q', String(search.value))
    }

    filters.value.forEach((filter: any) => {
      let selectedOptions: any = []
      const urlFilter = meta.value?.filters?.find((urlFilter: any) => urlFilter.key === filter.key)

      switch (filter.type) {
        case 'range':
          if ((filter.minSelected && filter.minSelected !== filter.min) || (filter.maxSelected && filter.maxSelected !== filter.max)) {
            params.set(filter.key, [filter.minSelected, filter.maxSelected].join('~'))
            setFilter = true
          }
          break
        default:
          if (filter?.options?.length) {
            selectedOptions = getDeepestActiveOptions(filter.options)
          }

          if (filter.key === 'category' && selectedOptions?.length && urlFilter) {
            selectedOptions = selectedOptions.filter((option: any) => !urlFilter.values.includes(option.key))
          }

          if (selectedOptions?.length) {
            params.set(filter.key, selectedOptions.map((option: any) => option.key).join('~'))
            setFilter = true
          }

          // Check if filter exists in url path and unset if it's not selected anymore
          if (!selectedOptions?.length && filter.key !== 'category') {
            if (urlFilter && filter.options?.find((option: any) => option.urlPath === lastBasePathPiece)) {
              basePathPieces.pop()
            }
          }

          break
      }
    })

    if (!setFilter || search.value) {
      params.delete('filter')
    }

    if (sort.value !== defaultSort) {
      params.set('sort', sort.value)
    }

    if (itemsPerPage.value !== defaultItemsPerPage) {
      params.set('limit', String(itemsPerPage.value))
    }

    const basePath = '/' + basePathPieces.join('/') + '/'
    const url = basePath + (params.toString().length > 0 ? '?' + params.toString() : '')

    return url
  }

  function optionToggle(filterKey: string, optionKey: string) {
    // console.log('optionToggle', filterKey, optionKey)

    // Direct filters manipulation for reactivity
    const filter: any | undefined = filters.value.find((filter: any) => filter.key === filterKey)
    const option: any | undefined = filter?.options?.find((option: any) => option.key === optionKey)

    if (option) {
      option.selected = !option.selected
    }

    if (applyOptionsDirectly.value) {
      filterApply(filterKey)
    }
  }

  function filterRangeResetMin(filterKey: string) {
    const filter: any | undefined = filters.value.find((filter: any) => filter.key === filterKey)

    if (filter) {
      filter.minSelected = filter.min
      filter.minToBeSelected = filter.min
    }
  }

  function filterRangeResetMax(filterKey: string) {
    const filter: any | undefined = filters.value.find((filter: any) => filter.key === filterKey)

    if (filter) {
      filter.maxSelected = filter.max
      filter.maxToBeSelected = filter.max
    }
  }

  // Single Filter Level
  function filterApply(filterKey: string) {
    // console.log('filterApply filterKey', filterKey)
    // Direct filters manipulation for reactivity
    const filter: any | undefined = filters.value.find((filter: any) => filter.key === filterKey)

    if (filter.type === 'range') {
      filter.minSelected = filter.minToBeSelected
      filter.maxSelected = filter.maxToBeSelected
    }
    else if (filter.options?.length) {
      filter.options.forEach((option: any) => {
        option.active = option.selected
      })
    }

    const url = generateUrlFromSelectedFilters()
    router.push(url)
  }

  function filterReset(filterKey: string) {
    // Direct filters manipulation for reactivity
    const filter: any | undefined = filters.value.find((filter: any) => filter.key === filterKey)

    if (filter.type === 'range') {
      // Step back
      filter.minToBeSelected = filter.minSelected || filter.min
      filter.maxToBeSelected = filter.maxSelected || filter.max
    }
    else if (filter.options?.length) {
      filter.options.forEach((option: any) => {
        // Step back
        option.selected = option.active
      })
    }
  }

  function filterRemove(filterKey: string) {
    // Direct filters manipulation for reactivity
    const filter: any | undefined = filters.value.find((filter: any) => filter.key === filterKey)

    if (filter.type === 'range') {
      // Clear options
      filter.minToBeSelected = filter.min
      filter.maxToBeSelected = filter.max
    }
    else if (filter.options?.length) {
      filter.options.forEach((option: any) => {
        option.selected = false
      })
    }
  }

  // All Filters Methods
  function filtersResetAll() {
    filters.value.forEach((filter: any) => {
      if (filter.type === 'range') {
        filter.minToBeSelected = filter.minSelected || filter.min
        filter.maxToBeSelected = filter.maxSelected || filter.max
      }
      else if (filter?.options?.length) {
        filter.options.forEach((option: any) => {
          option.selected = option.active
        })
      }
    })
  }

  function filtersRemoveAll() {
    filters.value.forEach((filter: any) => {
      if (filter.type === 'range') {
        filter.minToBeSelected = filter.min
        filter.maxToBeSelected = filter.max
      }
      else if (filter?.options?.length) {
        filter.options = filter.options.forEach((option: any) => {
          option.selected = false
        })
      }
    })
  }

  function filtersApplyAll() {
    filters.value.forEach((filter: any) => {
      if (filter.type === 'range') {
        filter.minSelected = filter.minToBeSelected
        filter.maxSelected = filter.maxToBeSelected
      }
      else if (filter?.options?.length) {
        filter.options.forEach((option: any) => {
          option.active = option.selected
        })
      }
    })

    const url = generateUrlFromSelectedFilters()
    router.push(url)
  }

  // Expecteed Result
  async function getExpectedTotal() {
    const lclFilters = [
      ...(meta.value?.filters || []),
    ]

    filters.value.forEach((filter: any) => {
      switch (filter.type) {
        case 'range':
          if ((filter.minToBeSelected && filter.minToBeSelected !== filter.min) || (filter.maxToBeSelected && filter.maxToBeSelected !== filter.max)) {
            lclFilters.push({
              key: filter.key,
              min: parseFloat(filter.minToBeSelected),
              max: parseFloat(filter.maxToBeSelected),
            })
          }
          break
        default:
          if (filter?.options?.length) {
            const selectedOptions = filter.options.filter((option: any) => option.selected).map((option: any) => option.key)

            if (selectedOptions.length) {
              lclFilters.push({
                key: filter.key,
                values: selectedOptions,
              })
            }
          }
          break
      }
    })

    const variables = {
      filters: Object.values(Object.assign({}, ...lclFilters.map((f) => {
        const filter = {
          ...f,
        }

        if (filter.__typename) {
          delete filter.__typename
        }

        return { [filter.key]: filter }
      },
      ))),
      search: search.value,
    }

    const { data: response } = await useAsyncGraphqlQuery('esProductListCount', variables, useGraphqlDefaultClient())
    expectedTotal.value = response?.value?.data?.esProductList?.totalCount || 0
    return response?.value?.data?.esProductList?.totalCount
  }

  return {
    generateUrlFromSelectedFilters,
    applyOptionsDirectly,

    // Filter Options Level
    optionToggle,
    filterRangeResetMin,
    filterRangeResetMax,

    // Single Filter Level
    filterApply,
    filterReset,
    filterRemove,

    // All Filters Level
    rawFilters,
    filters,
    filtersApplyAll,
    filtersResetAll,
    filtersRemoveAll,
    filtersFromRoute,
    activeFilters,
    activeFiltersCount,
    expectedTotal,
    getExpectedTotal,
  }
}
